/**
 * Hook for fetching a single group's allocation data for a specific date.
 * Uses the weekly calendar API with days=1 to get children and utilization.
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { CapacityService } from '@/api/services/CapacityService';
import type { capacity_WeekCalendarResponse } from '@/api';
import type { CalendarChildView } from '@/types/calendar';

export interface GroupDayAllocations {
  children: CalendarChildView[];
  hasServiceBreakdown: boolean;
  peakService?: string;
}

/**
 * Fetches allocation data for a specific group on a specific date.
 * Only enabled when the dialog is open to avoid unnecessary requests.
 */
export function useGroupDayAllocations(
  groupId: string,
  date: string,
  options?: { enabled?: boolean }
) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: ['capacity', 'group-day', groupId, date],
    queryFn: async (): Promise<GroupDayAllocations> => {
      const response: capacity_WeekCalendarResponse =
        await CapacityService.getApiV1CapacityCalendarWeek(date, undefined, 1);

      // Find the matching group in the response
      const dayData = response.days?.[0];
      const groupData = dayData?.groups?.find((g) => g.group_id === groupId);

      if (!groupData) {
        return { children: [], hasServiceBreakdown: false };
      }

      const children: CalendarChildView[] = (groupData.allocations ?? []).map(
        (alloc) => ({
          id: alloc.child_id ?? '',
          name: `${alloc.child_firstname ?? ''} ${alloc.child_lastname ?? ''}`.trim(),
          services: alloc.services ?? [],
          allocationId: alloc.allocation_id ?? '',
          ageTypeCode: alloc.age_type_code ?? 'CHILD',
          avatarUrl: alloc.avatarUrl,
          childInternalId: alloc.childInternalId,
          isAbsent: alloc.is_absent ?? false,
          hasOverride: alloc.has_override ?? false,
          overrideGroupId: alloc.override_group_id,
          isExtraDaycare: alloc.is_extra_daycare ?? false,
          absenceReason: alloc.absence_reason,
          absenceIsFullDay: alloc.absence_is_full_day,
          birthdate: alloc.birthdate,
          allocationFromDate: alloc.allocation_from_date,
          allocationToDate: alloc.allocation_to_date,
          isJoiningThisDay: alloc.is_joining_this_day,
          isLeavingThisDay: alloc.is_leaving_this_day,
        })
      );

      return {
        children,
        hasServiceBreakdown: groupData.utilization?.has_service_breakdown ?? false,
        peakService: groupData.utilization?.peak_service,
      };
    },
    enabled:
      (options?.enabled ?? false) &&
      !!groupId &&
      !!date &&
      isSignedIn &&
      isAuthReady,
    staleTime: 2 * 60 * 1000,
    retry: (failureCount, error) => {
      if (
        error instanceof Error &&
        (error.message.includes('401') ||
          error.message.includes('403') ||
          error.message.includes('404'))
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });
}
